import { Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import { useFeature } from '@growthbook/growthbook-react'

import { MAX_PARTICIPANT_IN_CALL } from 'config'

import { getConferenceCallUrl } from 'lib/conferenceCall'
import { CallJoinableMode, GroupCallJoinableMode, Post } from 'lib/_types'

import { selectIsLoggedIn } from 'store/selectors'

import useOpenConferenceCall from 'features/ConferenceCall/hooks/useConferenceCall'
import usePageModal from 'features/Modal/hooks/usePageModal'

import { MODAL_MODE } from 'components/Modal/config'
import Avatar from 'components/Avatar'
import ImageLoader from 'components/ImageLoader'
import PostCallGiftSummary from './PostCallGiftSummary'

import './style.scss'

type Props = {
  post: Post
  disabled?: boolean
}

const PostCall = ({ post, disabled = false }: Props) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const { t } = useTranslation()
  const { openConferenceCall } = useOpenConferenceCall()
  const { open } = usePageModal()

  const isEmplGiftsEnabled = useFeature('yay-17610')?.on

  const conferenceCallUsers = useMemo(() => {
    const totalAnonUserCounts =
      post?.conference_call?.anonymous_call_users_count ?? 0
    const conferenceCallUsers = [
      ...(post?.conference_call?.conference_call_users || []),
    ]

    for (let i = 0; i < totalAnonUserCounts; i++) {
      conferenceCallUsers.push({
        id: Number(`10${i}`), // placeholder anon id because id is called on this array later, expecting User type
        profile_icon_thumbnail: '/images/avatar-anon.png',
        nickname: t('common.anonymous'),
      })
    }

    return conferenceCallUsers
  }, [post?.conference_call])

  const isFull =
    conferenceCallUsers.length >=
    (post?.conference_call?.max_participants ??
      (post?.conference_call?.call_type === 'vdo'
        ? MAX_PARTICIPANT_IN_CALL.VIDEO
        : MAX_PARTICIPANT_IN_CALL.VOICE)) // sometime server return null so it need to be hardcoded on ui

  const onOpenCall = () => {
    if (post?.conference_call?.active) {
      if (
        !isLoggedIn &&
        post?.conference_call?.joinable_by !== CallJoinableMode.Anyone
      ) {
        open(MODAL_MODE.LOGIN, {
          redirectUrl: getConferenceCallUrl(
            post?.conference_call?.id?.toString?.(),
            true
          ),
        })
        return
      }

      openConferenceCall({
        conferenceCallId: post?.conference_call?.id?.toString?.(),
      })
    }
  }

  const getCallDescription = (
    joinableBy: CallJoinableMode | GroupCallJoinableMode
  ) => {
    if (isFull) return t('conference_call.full')

    switch (joinableBy) {
      case CallJoinableMode.FollowersOnly:
        return t('common.only_follower_can_join')

      case CallJoinableMode.InviteOnly:
      case GroupCallJoinableMode.InviteOnly:
        return t('common.only_invite_can_join')

      case CallJoinableMode.MutualFollowsOnly:
        return t('common.mutual_follows_only_can_join')

      case CallJoinableMode.Noone:
      case GroupCallJoinableMode.Noone:
        return t('common.no_one_title')

      case CallJoinableMode.Users:
      case GroupCallJoinableMode.Users:
        return t('common.only_yay_user_can_join')

      case CallJoinableMode.Anyone:
      default:
        return t('post.post_call.joinable_by.else')
    }
  }

  if (post?.post_type !== 'call') {
    return null
  }

  return (
    <Fragment>
      <div
        className={cx('PostCall', {
          'PostCall--video': post?.conference_call?.call_type === 'vdo',
          'PostCall--disabled': disabled,
        })}
      >
        <a
          onClick={isFull ? () => {} : onOpenCall}
          className={cx(
            'PostCall__wrapper',
            post?.conference_call?.active && 'PostCall__wrapper--active',
            isFull && 'PostCall__wrapper--full'
          )}
        >
          {post?.conference_call?.active ? (
            <Fragment>
              <div>
                <strong>
                  {getCallDescription(post?.conference_call?.joinable_by)}
                </strong>

                {!!post?.conference_call?.game && (
                  <div className="PostCall__game">
                    <ImageLoader
                      className="PostCall__game__icon"
                      url={post?.conference_call?.game?.icon_url}
                      alt={post?.conference_call?.game?.title}
                    />
                    <div className="PostCall__game__title">
                      {t('common.something_opening', {
                        title: post?.conference_call?.game?.title,
                      })}
                    </div>
                  </div>
                )}

                {!!post?.conference_call?.genre && (
                  <div className="PostCall__game">
                    <ImageLoader
                      className="PostCall__game__icon"
                      url={post?.conference_call?.genre?.icon_url}
                      alt={post?.conference_call?.genre?.title}
                    />
                    <div className="PostCall__game__title">
                      {post?.conference_call?.genre?.title}
                    </div>
                  </div>
                )}
              </div>

              <span>
                {t('post.post_call.user_count', {
                  number:
                    post?.conference_call?.conference_call_users_count || 1,
                })}
              </span>

              <ul className="PostCall__ul">
                {conferenceCallUsers.map((user, i) => (
                  <li
                    className={cx(
                      'PostCall__ul__li',
                      `PostCall__ul__li--role-${
                        (
                          post?.conference_call?.conference_call_user_roles ||
                          []
                        ).find(({ user_id }) => user_id === user.id)?.role ||
                        'participant'
                      }`
                    )}
                    key={i}
                  >
                    <Avatar user={user} />
                  </li>
                ))}
              </ul>
            </Fragment>
          ) : (
            <Fragment>
              <strong>{t('post.post_call.inactive_call.title')}</strong>
              <span>{t('post.post_call.inactive_call.description')}</span>
            </Fragment>
          )}
        </a>
        {/* YAY-17628 call to /gifts from this component returns 401 error if not logged in */}
        {isLoggedIn && isEmplGiftsEnabled && post?.gifts_count?.length > 0 && (
          <PostCallGiftSummary gifts={post?.gifts_count} />
        )}
      </div>
    </Fragment>
  )
}

export default PostCall
